import { addYears, isWithinInterval } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import { NewBookingContext } from '../components';
import { ToggleDisplayMode } from '../types/toggleDisplayMode';
import { ReducerContext } from './reducer';
export const useManagerApprovalAndDeductEntitlement = () => {
    const { updateState, deductFromEntitlement, dateRange: { startDate, endDate }, } = useContext(NewBookingContext);
    const { userSettingsState } = useContext(ReducerContext);
    const [sendForApprovalDisplayMode, setSendForApprovalDisplayMode] = useState(ToggleDisplayMode.None);
    const [deductionDisplayMode, setDeductionDisplayMode] = useState(ToggleDisplayMode.None);
    const [isInPreviousEntitlementYear, setIsInPreviousEntitlementYear] = useState(false);
    const [isInCurrentEntitlementYear, setIsInCurrentEntitlementYear] = useState(false);
    const [isInFutureEntitlementYear, setIsInFutureEntitlementYear] = useState(false);
    const [hasSetupRun, setHasSetupRun] = useState(false);
    const isInPast = useMemo(() => {
        if (!startDate) {
            return;
        }
        return (endDate?.toDate() ?? startDate.toDate()) < new Date();
    }, [endDate, startDate]);
    useEffect(() => {
        if (!startDate) {
            return;
        }
        const startOfCurrentHolidayYear = new Date(userSettingsState.start);
        const endOfCurrentHolidayYear = new Date(userSettingsState.end);
        const endOfLastHolidayYear = addYears(endOfCurrentHolidayYear, -1);
        const date = endDate?.toDate() ?? startDate.toDate();
        setIsInPreviousEntitlementYear(date < endOfLastHolidayYear);
        setIsInCurrentEntitlementYear(isWithinInterval(startDate.toDate(), {
            start: startOfCurrentHolidayYear,
            end: endOfCurrentHolidayYear,
        }));
        setIsInFutureEntitlementYear(date > endOfCurrentHolidayYear);
        setHasSetupRun(true);
    }, [endDate, startDate, userSettingsState.end, userSettingsState.start]);
    useEffect(() => {
        if (!hasSetupRun) {
            return;
        }
        if (isInPreviousEntitlementYear) {
            setSendForApprovalDisplayMode(ToggleDisplayMode.Hidden);
            setDeductionDisplayMode(ToggleDisplayMode.Hidden);
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: false,
                deductFromEntitlement: true,
            }));
            return;
        }
        if (isInCurrentEntitlementYear && isInPast) {
            setDeductionDisplayMode(ToggleDisplayMode.Active);
            setSendForApprovalDisplayMode(ToggleDisplayMode.Active);
            return;
        }
        if ((isInCurrentEntitlementYear && !isInPast) || isInFutureEntitlementYear) {
            if (userSettingsState.managerEmail) {
                setSendForApprovalDisplayMode(ToggleDisplayMode.Disabled);
            }
            else {
                setSendForApprovalDisplayMode(ToggleDisplayMode.Active);
            }
            updateState((prevState) => ({
                ...prevState,
                deductFromEntitlement: true,
                isManagerApprovalRequired: !!userSettingsState.managerEmail,
            }));
            setDeductionDisplayMode(ToggleDisplayMode.Disabled);
            return;
        }
        if (isInPast && !isInCurrentEntitlementYear) {
            setDeductionDisplayMode(ToggleDisplayMode.Active);
            setSendForApprovalDisplayMode(ToggleDisplayMode.Disabled);
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: false,
                deductFromEntitlement: false,
            }));
            return;
        }
        if (userSettingsState.managerEmail) {
            setSendForApprovalDisplayMode(ToggleDisplayMode.Disabled);
            updateState((prevState) => ({
                ...prevState,
                isManagerApprovalRequired: deductFromEntitlement,
            }));
        }
        setDeductionDisplayMode(ToggleDisplayMode.Active);
    }, [
        isInPreviousEntitlementYear,
        isInCurrentEntitlementYear,
        isInFutureEntitlementYear,
        isInPast,
        userSettingsState.managerEmail,
        updateState,
        userSettingsState.end,
        deductFromEntitlement,
        hasSetupRun,
    ]);
    return {
        sendForApprovalDisplayMode,
        deductionDisplayMode,
    };
};
